import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api } from "../utils/api";
import { tTax } from "../utils/taxonomyI18n";
import PostCard from "../components/PostCard";
import PowerCard from "../components/PowerCard";
import BookingModal from "../components/BookingModal";
import PhotoLightbox, { GalleryPhoto } from "../components/PhotoLightbox";
import VerifiedBadge from "../components/VerifiedBadge";
import VerificationTracker from "../components/VerificationTracker";
import { formatScore } from "../utils/format";
import { usePaymentReturnBanner } from "../utils/paymentReturn";
import { Lock, CalendarDays, MessageCircle } from "lucide-react";
import { t } from "../utils/i18n";
import RingsIcon from "../components/date/RingsIcon";
import DatePromptSheet from "../components/date/DatePromptSheet";
import DateOrganizerSheet from "../components/date/DateOrganizerSheet";
import DateResponseSheet from "../components/date/DateResponseSheet";
import { useDateState } from "../hooks/useDateState";
import { useIsMobile } from "../hooks/useIsMobile";

const TABS = ["Profil","Infos","Passions","Powers","Amis","Rituels","Communautés","Galerie"];

const PALETTE = ["#0a0a0a", "#0a0a0a", "#0a0a0a", "#333333", "#666666", "#777777", "#4d4d4d"];
const colorFromId = (id: string) => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return PALETTE[Math.abs(hash) % PALETTE.length];
};
const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

const HeartIcon = ({ filled }: { filled?: boolean }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={filled ? "#0a0a0a" : "none"} stroke="#666" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 21s-6.7-4.3-9.3-8.2C1 10 1.6 6.6 4.4 5.1c2.3-1.2 4.9-.4 6.3 1.6.4.5.9 1.1 1.3 1.7.4-.6.9-1.2 1.3-1.7 1.4-2 4-2.8 6.3-1.6 2.8 1.5 3.4 4.9 1.7 7.7C18.7 16.7 12 21 12 21z"/>
  </svg>
);
const ShareIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="2.6"/><circle cx="6" cy="12" r="2.6"/><circle cx="18" cy="19" r="2.6"/>
    <line x1="8.3" y1="10.6" x2="15.6" y2="6.6"/><line x1="8.3" y1="13.4" x2="15.6" y2="17.4"/>
  </svg>
);
const ChatIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.4 8.4 0 0 1-8.4 8.4 8.3 8.3 0 0 1-3.8-.9L3 21l1.9-5.8a8.3 8.3 0 0 1-.9-3.8A8.4 8.4 0 0 1 12.5 3a8.4 8.4 0 0 1 8.4 8.4z"/>
  </svg>
);
const AddFriendIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/>
    <line x1="19" y1="8" x2="19" y2="14"/><line x1="16" y1="11" x2="22" y2="11"/>
  </svg>
);
const CheckFriendIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/>
    <polyline points="16 11 18 13 22 9"/>
  </svg>
);
const ClockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/>
  </svg>
);
const PinIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 6-9 12-9 12s-9-6-9-12a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const MoreIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="12" cy="19" r="1.8"/>
  </svg>
);
const BriefcaseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
  </svg>
);
const GalleryBigIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 8.5v10A2.5 2.5 0 0 0 5.5 21h10"/>
    <rect x="6" y="3" width="15" height="15" rx="2.5"/>
    <circle cx="11" cy="8" r="1.4"/>
    <path d="M6.5 15l3.5-3.8a1.3 1.3 0 0 1 1.9-.05L15 15"/>
  </svg>
);
// Étoile fine (contour) — plus épurée que le glyphe ★ utilisé ailleurs, réservée aux petits
// badges de compteur sur les miniatures de la galerie.
const ThinStarIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2.5 15.1 9 22 10 17 15.1 18.2 22 12 18.6 5.8 22 7 15.1 2 10 8.9 9" />
  </svg>
);
const EyeThinIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);


export default function ProfileVisitor() {
  const { lang, user } = useApp();
  const { id } = useParams();
  const location = useLocation();
  usePaymentReturnBanner();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState("Profil");

  const [profile, setProfile] = useState<any>(null);
  const [passions, setPassions] = useState<any[]>([]);
  const [powers, setPowers] = useState<any[]>([]);
  const [bookingFor, setBookingFor] = useState<{ id: string; label: string } | null>(null);
  const [identityBadges, setIdentityBadges] = useState<{ family: string; label: string; count: number }[]>([]);
  const [ratingBreakdown, setRatingBreakdown] = useState<{ ponctualite: string; respect: string; fiabilite: string; recommandation: string; ratings_count: string } | null>(null);
  const [friends, setFriends] = useState<any[]>([]);
  const [friendsHidden, setFriendsHidden] = useState(false);
  const [rituals, setRituals] = useState<any[]>([]);
  const [communities, setCommunities] = useState<any[]>([]);
  const [publications, setPublications] = useState<any[]>([]);
  const [photos, setPhotos] = useState<GalleryPhoto[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [myPassions, setMyPassions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [restricted, setRestricted] = useState(false);
  const [friendshipStatus, setFriendshipStatus] = useState<"none" | "accepted" | "pending_sent" | "pending_received">("none");
  const [friendshipId, setFriendshipId] = useState<string | null>(null);
  const [badgeExpanded, setBadgeExpanded] = useState(false);
  const [shared, setShared] = useState(false);
  const [isBlockedByMe, setIsBlockedByMe] = useState(false);
  const [isHiddenByMe, setIsHiddenByMe] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // Like → Match → Date : état géré côté serveur (voir hooks/useDateState.ts), amorcé sans
  // requête supplémentaire dès que GET /users/:id répond (hydrate ci-dessous).
  const dateState = useDateState(id || "");
  const [showDatePrompt, setShowDatePrompt] = useState(false);
  const [showDateOrganizer, setShowDateOrganizer] = useState(false);
  const [showDateResponse, setShowDateResponse] = useState(false);
  const [coupleNotice, setCoupleNotice] = useState<string | null>(null);
  const coupleNoticeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showCoupleNotice = (msg: string) => {
    setCoupleNotice(msg);
    if (coupleNoticeTimeoutRef.current) clearTimeout(coupleNoticeTimeoutRef.current);
    coupleNoticeTimeoutRef.current = setTimeout(() => setCoupleNotice(null), 3500);
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setNotFound(false);
    setRestricted(false);
    api.get(`/users/${id}`).then(data => {
      if (!data || !data.user) { setNotFound(true); return; }
      setProfile(data.user);
      setRestricted(!!data.restricted);
      setPassions(data.passions || []);
      setPowers(data.powers || []);
      setIdentityBadges(data.identity_badges || []);
      setRatingBreakdown(data.rating_breakdown || null);
      setFriendshipStatus(data.user.friendship_status || "none");
      setFriendshipId(data.user.friendship_id || null);
      setIsBlockedByMe(!!data.user.is_blocked_by_me);
      setIsHiddenByMe(!!data.user.is_hidden_by_me);
      dateState.hydrate(data.user);
    }).catch(() => setNotFound(true)).finally(() => setLoading(false));
    api.get("/users/me").then(data => { if (data?.passions) setMyPassions(data.passions); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Arrivée depuis la carte "Match" d'une notification (Notifications.tsx navigue avec cet état)
  // : ouvre directement la sheet "Un rendez-vous ?" une fois le profil chargé.
  useEffect(() => {
    if ((location.state as any)?.openDateSheet && dateState.state === "matched") {
      setShowDatePrompt(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateState.state, location.state]);

  useEffect(() => {
    if (!id) return;
    if (activeTab === "Amis" && friends.length === 0) {
      api.get(`/users/${id}/friends`).then(data => { setFriends(data?.friends || []); setFriendsHidden(!!data?.hidden); }).catch(() => {});
    }
    if (activeTab === "Rituels" && rituals.length === 0) {
      api.get(`/users/${id}/rituals`).then(data => setRituals(data?.rituals || [])).catch(() => {});
    }
    if (activeTab === "Communautés" && communities.length === 0) {
      api.get(`/users/${id}/communities`).then(data => setCommunities(data?.communities || [])).catch(() => {});
    }
    if (activeTab === "Profil" && publications.length === 0) {
      api.get(`/users/${id}/publications`).then(data => setPublications((data?.publications || []).filter((p: any) => !["power_request", "community", "ritual", "welcome", "move"].includes(p.type)))).catch(() => {});
    }
    if (activeTab === "Galerie" && photos.length === 0) {
      api.get(`/users/${id}/photos`).then(data => setPhotos(data?.photos || [])).catch(() => {});
    }
  }, [activeTab, id, friends.length, rituals.length, communities.length, publications.length, photos.length]);

  const tabContentRef = useRef<HTMLDivElement>(null);
  const goToTab = (tab: string) => {
    setActiveTab(tab);
    setTimeout(() => tabContentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const handleLikeButton = async () => {
    if (!id) return;
    if (dateState.state === "couple") {
      showCoupleNotice(t(lang, "like_toast_couple_short"));
      return;
    }
    if (dateState.state === "matched") { setShowDatePrompt(true); return; }
    if (dateState.state === "invitation_received") { setShowDateResponse(true); return; }
    if (dateState.state === "confirmed") { startConversation(); return; }
    if (dateState.state === "none" || dateState.state === "liked_by_them") {
      const data = await dateState.like();
      if (data?.code === "IN_RELATIONSHIP") showCoupleNotice(t(lang, "like_toast_couple"));
    }
  };

  const handleFriendAction = () => {
    if (!id) return;
    if (friendshipStatus === "none") {
      setFriendshipStatus("pending_sent");
      // Optimiste, mais who_can_invite peut désormais rejeter la demande (403) : sans ce
      // contrôle, l'UI resterait bloquée sur "Demande envoyée" alors que rien n'a été créé.
      api.post(`/users/${id}/friend-request`, {}).then(data => {
        if (data?.error) { setFriendshipStatus("none"); alert(data.error); }
      }).catch(() => {});
    } else if (friendshipStatus === "pending_received" && friendshipId) {
      setFriendshipStatus("accepted");
      api.put(`/users/friend-request/${friendshipId}/accept`, {}).catch(() => {});
    } else if (friendshipStatus === "accepted") {
      if (!window.confirm(`Retirer ${profile?.name} de tes amis ?`)) return;
      setFriendshipStatus("none");
      api.delete(`/users/${id}/friend`).catch(() => {});
    }
  };

  const rejectFriendRequest = () => {
    if (!friendshipId) return;
    setFriendshipStatus("none");
    api.delete(`/users/friend-request/${friendshipId}/reject`).catch(() => {});
  };

  const blockUser = () => {
    if (!id || !window.confirm(`Bloquer ${profile?.name} ? Vous ne pourrez plus vous contacter, ni vous voir en amis.`)) return;
    setShowMoreMenu(false);
    api.post(`/users/${id}/block`, {}).then(() => {
      setIsBlockedByMe(true);
      setFriendshipStatus("none");
    }).catch(() => {});
  };

  // Masquer — différent d'un blocage : à sens unique, jamais notifié, la personne masquée
  // continue de voir/contacter normalement (voir plan "Trois emprunts...").
  const hideUser = () => {
    if (!id) return;
    setShowMoreMenu(false);
    api.post(`/users/${id}/hide`, {}).then(() => setIsHiddenByMe(true)).catch(() => {});
  };

  const unhideUser = () => {
    if (!id) return;
    setShowMoreMenu(false);
    api.delete(`/users/${id}/hide`).then(() => setIsHiddenByMe(false)).catch(() => {});
  };

  const unblockUser = () => {
    if (!id) return;
    setShowMoreMenu(false);
    api.delete(`/users/${id}/block`).then(() => setIsBlockedByMe(false)).catch(() => {});
  };

  const startConversation = () => {
    if (!id) return;
    api.post(`/messages/direct/${id}`, {}).then(data => {
      if (data?.conversationId) navigate(`/messages/${data.conversationId}`);
      else if (data?.error) alert(data.error);
    }).catch(() => {});
  };

  const shareProfile = async () => {
    const url = `${window.location.origin}/profile/${id}`;
    if (navigator.share) {
      try { await navigator.share({ title: profile?.name, url }); return; } catch { return; }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    } catch {}
  };

  // Même logique de réaction et de participation que le fil d'accueil (PostCard partagé).
  const toggleReaction = (post: any, type: "interested" | "top" | "envieux") => {
    api.post(`/publications/${post.id}/react`, { type }).then(data => {
      if (data?.action) {
        setPublications(prev => prev.map(p => p.id === post.id ? {
          ...p,
          tops_count: data.tops_count ?? p.tops_count,
          envieux_count: data.envieux_count ?? p.envieux_count,
          interested_count: data.interested_count ?? p.interested_count,
          my_reactions: data.action === "added"
            ? [...(p.my_reactions || []), type]
            : (p.my_reactions || []).filter((r: string) => r !== type),
        } : p));
      }
    }).catch(() => {});
  };

  const participate = (post: any) => {
    if (post.my_participation_status) return;
    const success_url = `${window.location.origin}${window.location.pathname}?payment=success`;
    const cancel_url = `${window.location.origin}${window.location.pathname}?payment=cancelled`;
    api.post(`/publications/${post.id}/participate`, { success_url, cancel_url, lang }).then(data => {
      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else if (data?.status) {
        setPublications(prev => prev.map(p => p.id === post.id ? { ...p, my_participation_status: data.status } : p));
      } else if (data?.error) {
        alert(data.error);
      }
    }).catch(() => {});
  };

  const card: React.CSSProperties = { backgroundColor: "white", borderRadius: 16, padding: 20, marginBottom: 12, border: "1px solid #F0F0F0" };
  const common = passions.filter(p => myPassions.some((mp: any) => mp.label?.toLowerCase() === p.label?.toLowerCase()));

  if (id && user?.id && id === user.id) {
    return <Navigate to="/profile" replace />;
  }
  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: "#999" }}>Chargement...</div>;
  }
  if (notFound || !profile) {
    return <div style={{ padding: 40, textAlign: "center", color: "#999" }}>Profil introuvable.</div>;
  }

  if (restricted) {
    const color = colorFromId(profile.id);
    return (
      <div style={{ maxWidth: 420, margin: "60px auto", padding: 20, textAlign: "center" }}>
        <div style={{ width: 84, height: 84, borderRadius: "50%", margin: "0 auto 16px", backgroundColor: color, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 26, overflow: "hidden" }}>
          {profile.avatar_url ? <img src={profile.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(profile.name)}
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{profile.name}</div>
        {profile.city && <div style={{ fontSize: 13, color: "#999", marginBottom: 20 }}>{profile.city}</div>}
        <div style={card}>
          <Lock size={22} color="#999" style={{ marginBottom: 10 }} />
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Ce profil est privé</div>
          <div style={{ fontSize: 13, color: "#999", marginBottom: 16 }}>{profile.name} a choisi de réserver son profil à ses amis.</div>
          {friendshipStatus === "none" && (
            <button onClick={handleFriendAction} style={{ padding: "10px 22px", borderRadius: 20, border: "none", backgroundColor: "#0a0a0a", color: "white", fontWeight: 600, fontSize: 13.5, cursor: "pointer" }}>Ajouter en ami</button>
          )}
          {friendshipStatus === "pending_sent" && <div style={{ fontSize: 13, color: "#999" }}>Demande d'ami envoyée</div>}
          {friendshipStatus === "pending_received" && (
            <button onClick={handleFriendAction} style={{ padding: "10px 22px", borderRadius: 20, border: "none", backgroundColor: "#0a0a0a", color: "white", fontWeight: 600, fontSize: 13.5, cursor: "pointer" }}>Accepter la demande</button>
          )}
        </div>
      </div>
    );
  }

  const friendLabel = friendshipStatus === "accepted" ? "Ami" : friendshipStatus === "pending_sent" ? "Demande envoyée" : friendshipStatus === "pending_received" ? "Accepter" : "Ajouter";
  const color = colorFromId(profile.id);

  return (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", minHeight: "calc(100vh - 60px)", overflow: isMobile ? "visible" : "hidden" }}>
      <div ref={contentRef} style={{ flex: 1, overflowY: "auto" }}>

        {/* COVER */}
        <div style={{ height: 200, background: profile.cover_url ? `url(${profile.cover_url}) center/cover` : `linear-gradient(135deg, ${color} 0%, #0a0a0a 100%)`, position: "relative" }}>
          <div style={{ position: "absolute", bottom: -40, left: 32, width: 80, height: 80, borderRadius: "50%", backgroundColor: color, border: "4px solid white", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 24, overflow: "hidden" }}>
            {profile.avatar_url ? <img src={profile.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(profile.name)}
          </div>
        </div>

        {/* INFOS */}
        <div style={{ ...card, borderRadius: "0 0 16px 16px", paddingTop: 52, marginBottom: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 22, fontWeight: 700 }}>{profile.name}</span>
                {profile.is_verified && (
                  <button onClick={() => setBadgeExpanded(true)} aria-label={t(lang, "vfy_track_title")} style={{ width: 22, height: 22, padding: 0, backgroundColor: "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                    <VerifiedBadge size={22} />
                  </button>
                )}
                {badgeExpanded && <VerificationTracker userId={profile.id} onClose={() => setBadgeExpanded(false)} />}
              </div>
              {identityBadges.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6, marginTop: 4 }}>
                  {identityBadges.map(b => (
                    <span key={b.family} title={`${b.count} expériences vécues en ${b.family}`} style={{ fontSize: 11, fontWeight: 700, color: "#0a0a0a", backgroundColor: "#F0F0F0", border: "1px solid #E5E5E5", borderRadius: 12, padding: "3px 10px" }}>
                      {b.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <button onClick={() => setShowMoreMenu(v => !v)} style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid #E0E0E0", backgroundColor: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#666" }}>
                <MoreIcon />
              </button>
              {showMoreMenu && (
                <>
                  <div onClick={() => setShowMoreMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
                  <div style={{ position: "absolute", top: "110%", right: 0, backgroundColor: "white", border: "1px solid #F0F0F0", borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.14)", overflow: "hidden", zIndex: 50, minWidth: 190 }}>
                    {isHiddenByMe ? (
                      <button onClick={unhideUser} style={{ display: "block", width: "100%", textAlign: "left", padding: "11px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#0a0a0a" }}>{t(lang, "profile_unhide_action") || "Réafficher ce profil"}</button>
                    ) : (
                      <button onClick={hideUser} style={{ display: "block", width: "100%", textAlign: "left", padding: "11px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#0a0a0a" }}>{t(lang, "profile_hide_action") || "Masquer ce profil"}</button>
                    )}
                    {isBlockedByMe ? (
                      <button onClick={unblockUser} style={{ display: "block", width: "100%", textAlign: "left", padding: "11px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#0a0a0a" }}>Débloquer ce profil</button>
                    ) : (
                      <button onClick={blockUser} style={{ display: "block", width: "100%", textAlign: "left", padding: "11px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#c0392b" }}>Bloquer ce profil</button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: "flex", backgroundColor: "#F8F8F8", borderRadius: 14, overflow: "hidden", marginBottom: 16, marginTop: 12 }}>
            {[{ num: formatScore(profile.score), label: "Score", onClick: () => navigate("/rating") }, { num: profile.friends_count, label: "Amis", onClick: () => goToTab("Amis") }, { num: profile.publications_count, label: "Publications", onClick: () => goToTab("Profil") }].map((s, i) => (
              <div key={i} onClick={s.onClick} style={{ flex: 1, textAlign: "center", padding: "14px 8px", borderRight: i < 2 ? "1px solid #F0F0F0" : "none", cursor: "pointer" }}>
                <div style={{ fontWeight: 800, fontSize: 20, color: "#0a0a0a" }}>{s.num}</div>
                <div style={{ fontSize: 11, color: "#999", marginTop: 2, textTransform: "uppercase", letterSpacing: 0.5 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Boutons interaction — dans cet ordre : Ajouter, Discuter, Partager, I like you.
              Masqués et remplacés par une bannière si le profil est bloqué. */}
          {isBlockedByMe ? (
            <div style={{ padding: "14px", borderRadius: 12, backgroundColor: "#F8F8F8", border: "1px solid #F0F0F0", marginBottom: 16, textAlign: "center" }}>
              <div style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>Tu as bloqué ce profil, plus d'ami, plus de messages entre vous.</div>
              <button onClick={unblockUser} style={{ padding: "9px 18px", borderRadius: 20, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Débloquer</button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <button onClick={handleFriendAction} disabled={friendshipStatus === "pending_sent"} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 4px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "#F5F5F5", cursor: friendshipStatus === "pending_sent" ? "default" : "pointer", opacity: friendshipStatus === "pending_sent" ? 0.7 : 1 }}>
                {friendshipStatus === "accepted" ? <CheckFriendIcon /> : friendshipStatus === "pending_sent" ? <ClockIcon /> : <AddFriendIcon />}
                <span style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>{friendLabel}</span>
              </button>
              {friendshipStatus === "pending_received" && (
                <button onClick={rejectFriendRequest} title="Refuser la demande" style={{ width: 38, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "#F5F5F5", cursor: "pointer", color: "#c0392b", fontSize: 15, fontWeight: 700 }}>✕</button>
              )}
              <button onClick={startConversation} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 4px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "#F5F5F5", cursor: "pointer" }}>
                <ChatIcon />
                <span style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>Discuter</span>
              </button>
              <button onClick={shareProfile} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 4px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "#F5F5F5", cursor: "pointer" }}>
                <ShareIcon />
                <span style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>{shared ? "Copié !" : "Partager"}</span>
              </button>
              <button
                onClick={handleLikeButton}
                disabled={dateState.state === "liked" || dateState.state === "invitation_sent"}
                style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 4px", borderRadius: 10,
                  border: "1px solid #E0E0E0", backgroundColor: "#F5F5F5",
                  cursor: (dateState.state === "liked" || dateState.state === "invitation_sent") ? "default" : "pointer",
                  opacity: (dateState.state === "liked" || dateState.state === "invitation_sent") ? 0.7 : 1,
                }}
              >
                {dateState.state === "couple" ? <RingsIcon size={15} />
                  : (dateState.state === "matched" || dateState.state === "invitation_sent" || dateState.state === "invitation_received") ? <CalendarDays size={15} color="#666" />
                  : dateState.state === "confirmed" ? <MessageCircle size={15} color="#666" />
                  : <HeartIcon filled={dateState.state === "liked"} />}
                <span style={{ fontSize: 12, fontWeight: 600, color: "#555", textTransform: dateState.state === "matched" || dateState.state === "confirmed" ? "uppercase" : "none" }}>
                  {dateState.state === "couple" ? t(lang, "like_btn_couple")
                    : dateState.state === "liked" ? t(lang, "like_btn_liked")
                    : dateState.state === "matched" ? t(lang, "like_btn_date")
                    : dateState.state === "invitation_sent" ? t(lang, "like_btn_invitation_sent")
                    : dateState.state === "invitation_received" ? t(lang, "like_btn_respond")
                    : dateState.state === "confirmed" ? t(lang, "like_btn_discuss")
                    : t(lang, "like_btn_like")}
                </span>
              </button>
            </div>
          )}

          {/* Séparateur : ville / fonction / bio */}
          {(profile.city || profile.job_title || profile.bio) && (
            <div style={{ borderTop: "1px solid #F0F0F0", borderBottom: "1px solid #F0F0F0", padding: "14px 2px", marginBottom: 16 }}>
              {(profile.city || profile.job_title) && (
                <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 16, marginBottom: profile.bio ? 8 : 0 }}>
                  {profile.job_title && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#666" }}>
                      <BriefcaseIcon /> {profile.job_title}
                    </div>
                  )}
                  {profile.city && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#666" }}>
                      <PinIcon /> {profile.city}
                    </div>
                  )}
                </div>
              )}
              {profile.bio && <p style={{ fontSize: 13, color: "#666", lineHeight: 1.7, margin: 0, maxWidth: 460 }}>{profile.bio}</p>}
            </div>
          )}

          {/* Passions / Powers */}
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            {["Passions", "Powers"].map(tab => (
              <button key={tab} onClick={() => goToTab(tab)} style={{ flex: 1, backgroundColor: "#0a0a0a", borderRadius: 14, padding: "14px", border: "none", cursor: "pointer", textAlign: "center" }}>
                <div style={{ color: "white", fontWeight: 800, fontSize: 13, letterSpacing: 1 }}>{tab.toUpperCase()}</div>
                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 10, marginTop: 3 }}>
                  {tab === "Passions" ? `${passions.length} centres d'intérêt` : `${powers.length} savoir-faire`}
                </div>
              </button>
            ))}
          </div>

          <button onClick={() => navigate("/rating")} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 14, padding: "12px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Évaluer {profile.name}
          </button>
        </div>

        {/* TABS */}
        <div style={{ display: "flex", backgroundColor: "white", borderBottom: "1px solid #E0E0E0", overflowX: "auto", position: "sticky", top: 0, zIndex: 10 }}>
          {TABS.map(tab => (
            <button key={tab} onClick={() => goToTab(tab)} style={{
              padding: "14px 16px", border: "none", cursor: "pointer", whiteSpace: "nowrap",
              backgroundColor: "transparent",
              borderBottom: activeTab === tab ? "2px solid #0a0a0a" : "2px solid transparent",
              color: activeTab === tab ? "#0a0a0a" : "#999",
              fontWeight: activeTab === tab ? 700 : 400, fontSize: 13,
            }}>{tab}</button>
          ))}
        </div>

        <div ref={tabContentRef} style={{ padding: 20, scrollMarginTop: 55 }}>

          {activeTab === "Profil" && (
            publications.length === 0
              ? <div style={{ ...card, textAlign: "center", color: "#999" }}>Aucune publication pour le moment</div>
              : publications.map(pub => <PostCard key={pub.id} post={pub} lang={lang} user={user} navigate={navigate} toggleReaction={toggleReaction} participate={participate} onHideAuthor={() => navigate("/")} />)
          )}

          {activeTab === "Infos" && (
            <div style={card}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Informations</div>
              {[
                { label: "Ville", value: profile.city },
                { label: "Métier", value: profile.job_title },
                { label: "Langues", value: (profile.languages || []).join(", ") },
                { label: "Âge", value: profile.birthday ? `${Math.floor((Date.now() - new Date(profile.birthday).getTime()) / (365.25 * 24 * 3600 * 1000))} ans` : "" },
                { label: "Sexe", value: { homme: "Homme", femme: "Femme", autre: "Autre" }[profile.gender as string] || "" },
                { label: "Statut marital", value: { celibataire: "Célibataire", en_couple: "En couple", marie: "Marié(e)", divorce: "Divorcé(e)", autre: "Autre" }[profile.marital_status as string] || "" },
              ].filter(info => info.value).map((info, i) => (
                <div key={i} style={{ display: "flex", gap: 12, padding: "12px 0", borderBottom: "1px solid #F5F5F5" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: "#999" }}>{info.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 500, marginTop: 2 }}>{info.value}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "Passions" && (
            <div style={card}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Passions de {profile.name}</div>
              <div style={{ fontSize: 12, color: "#999", marginBottom: 16 }}>Contacte {profile.name} pour partager un centre d'intérêt</div>
              {passions.length === 0 && <div style={{ color: "#999", fontSize: 13 }}>Aucune passion renseignée</div>}
              {passions.map((passion, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "13px 0", borderBottom: "1px solid #F5F5F5" }}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ color: "white", fontSize: 11, fontWeight: 700 }}>{passion.label.slice(0,2).toUpperCase()}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{tTax(lang, passion.label)}</div>
                    <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>Centre d'intérêt</div>
                  </div>
                  <button onClick={startConversation} style={{ padding: "6px 14px", borderRadius: 20, border: "1px solid #0a0a0a", backgroundColor: "transparent", color: "#0a0a0a", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Contacter</button>
                </div>
              ))}
            </div>
          )}

          {activeTab === "Powers" && (
            <div style={card}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Powers de {profile.name}</div>
              <div style={{ fontSize: 12, color: "#999", marginBottom: 16 }}>Contacte {profile.name} pour un savoir-faire</div>
              {ratingBreakdown && Number(ratingBreakdown.ratings_count) > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8, marginBottom: 18, padding: 12, backgroundColor: "#F8F8F8", borderRadius: 12 }}>
                  {[
                    { label: "Ponctualité", value: ratingBreakdown.ponctualite },
                    { label: "Respect", value: ratingBreakdown.respect },
                    { label: "Fiabilité", value: ratingBreakdown.fiabilite },
                    { label: "Recommandation", value: ratingBreakdown.recommandation },
                  ].map(dim => (
                    <div key={dim.label} style={{ flex: "1 1 40%", minWidth: 100 }}>
                      <div style={{ fontSize: 11, color: "#999" }}>{dim.label}</div>
                      <div style={{ fontSize: 15, fontWeight: 700 }}>{formatScore(dim.value)} <span style={{ fontSize: 11, color: "#bbb", fontWeight: 400 }}>/10</span></div>
                    </div>
                  ))}
                  <div style={{ width: "100%", fontSize: 11, color: "#999", marginTop: 2 }}>Basé sur {ratingBreakdown.ratings_count} évaluation{Number(ratingBreakdown.ratings_count) > 1 ? "s" : ""}</div>
                </div>
              )}
              {powers.length === 0 && <div style={{ color: "#999", fontSize: 13 }}>Aucun power renseigné</div>}
              {powers.map((power: any) => (
                <PowerCard
                  key={power.id}
                  power={{ id: power.id, label: power.label, years_experience: power.years_experience, description: power.description, photos: power.photos || [], missions_count: power.missions_count || 0 }}
                  isOwner={false}
                  onBook={() => setBookingFor({ id: power.id, label: power.label })}
                />
              ))}
              {bookingFor && (
                <BookingModal
                  resource={{ type: "power", id: bookingFor.id, label: bookingFor.label }} providerName={profile.name}
                  onClose={() => setBookingFor(null)}
                  onBooked={() => {}}
                />
              )}
            </div>
          )}

          {activeTab === "Amis" && (
            <div style={card}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>{profile.friends_count} amis</div>
              {friendsHidden && <div style={{ color: "#999", fontSize: 13 }}>{profile.name} a choisi de garder sa liste d'amis privée.</div>}
              {!friendsHidden && friends.length === 0 && <div style={{ color: "#999", fontSize: 13 }}>Aucun ami pour le moment</div>}
              {!friendsHidden && friends.map(friend => (
                <div key={friend.id} onClick={() => navigate("/profile/" + friend.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #F5F5F5", cursor: "pointer" }}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", backgroundColor: colorFromId(friend.id), display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 14, overflow: "hidden" }}>
                    {friend.avatar_url ? <img src={friend.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(friend.name)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{friend.name}</div>
                    <div style={{ fontSize: 12, color: "#0a0a0a", marginTop: 2 }}>★ {formatScore(friend.score)} / 10</div>
                  </div>
                  <span style={{ color: "#999", fontSize: 18 }}>›</span>
                </div>
              ))}
            </div>
          )}

          {activeTab === "Rituels" && (
            <div style={card}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Rituels de {profile.name}</div>
              {rituals.length === 0 && <div style={{ color: "#999", fontSize: 13 }}>Aucun rituel pour le moment</div>}
              {rituals.map(ritual => (
                <div key={ritual.id} onClick={() => navigate(`/ritual/${ritual.id}`)} style={{ backgroundColor: "#F8F8F8", borderRadius: 14, padding: 16, marginBottom: 12, cursor: "pointer" }}>
                  <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{ritual.title}</div>
                      <div style={{ fontSize: 12, color: "#666", marginBottom: 2 }}>{ritual.frequency}</div>
                      {ritual.location_name && <div style={{ fontSize: 12, color: "#666" }}>{ritual.location_name}</div>}
                    </div>
                    <div style={{ backgroundColor: "#F0F0F0", borderRadius: 10, padding: "6px 10px", textAlign: "center" }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: "#333333" }}>{ritual.streak}</div>
                      <div style={{ fontSize: 9, color: "#333333" }}>sem.</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 10, borderTop: "1px solid #E0E0E0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 12, color: "#666" }}>{ritual.members_count} participants</span>
                      {ritual.audience === "public" && <span style={{ backgroundColor: "#F0F0F0", borderRadius: 20, padding: "2px 8px", fontSize: 10, color: "#0a0a0a", fontWeight: 600 }}>Public</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "Communautés" && (
            <div style={card}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Communautés de {profile.name}</div>
              {communities.length === 0 && <div style={{ color: "#999", fontSize: 13 }}>Aucune communauté pour le moment</div>}
              {communities.map(community => (
                <div key={community.id} onClick={() => navigate("/community/" + community.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #F5F5F5", cursor: "pointer" }}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", backgroundColor: colorFromId(community.id), display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                    {community.cover_url ? <img src={community.cover_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 20 }}>👥</span>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{community.name}</div>
                    <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>{community.members_count?.toLocaleString()} membres</div>
                  </div>
                  <span style={{ color: "#999", fontSize: 18 }}>›</span>
                </div>
              ))}
            </div>
          )}

          {activeTab === "Galerie" && (
            <div style={card}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Galerie de {profile.name}</div>
              {photos.length === 0 ? (
                <div style={{ textAlign: "center", padding: "24px 0", color: "#999" }}>
                  <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><GalleryBigIcon /></div>
                  <div style={{ fontSize: 13 }}>Aucune photo pour le moment</div>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 }}>
                  {photos.map((p, i) => (
                    <div key={p.id} onClick={() => setLightboxIndex(i)} style={{ position: "relative", aspectRatio: "1", borderRadius: 8, overflow: "hidden", cursor: "pointer", backgroundColor: "#F0F0F0", ...(p.media_type !== "video" ? { backgroundImage: `url(${p.url})`, backgroundSize: "cover", backgroundPosition: "center" } : {}) }}>
                      {p.media_type === "video" && (
                        <>
                          <video src={p.url} muted preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                          <span style={{ position: "absolute", top: 6, left: 6, fontSize: 9.5, fontWeight: 800, color: "white", backgroundColor: "#e74c3c", borderRadius: 5, padding: "2px 5px", letterSpacing: 0.5 }}>(live)</span>
                          <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <span style={{ width: 30, height: 30, borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="white"><polygon points="6,4 20,12 6,20" /></svg>
                            </span>
                          </span>
                        </>
                      )}
                      {(Number(p.likes_count) > 0 || (p.media_type === "video" && !!p.views_count)) && (
                        <div style={{ position: "absolute", bottom: 6, left: 6, right: 6, display: "flex", justifyContent: "space-between" }}>
                          {Number(p.likes_count) > 0 && (
                            <span style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 10.5, fontWeight: 700, color: "white", textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}>
                              <ThinStarIcon /> {p.likes_count}
                            </span>
                          )}
                          {p.media_type === "video" && !!p.views_count && (
                            <span style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 10.5, fontWeight: 700, color: "white", textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}>
                              <EyeThinIcon /> {p.views_count}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {lightboxIndex !== null && (
            <PhotoLightbox
              photos={photos}
              index={lightboxIndex}
              onIndexChange={setLightboxIndex}
              onClose={() => setLightboxIndex(null)}
              isOwner={false}
              currentUserId={user?.id}
              ownerId={profile.id}
              ownerName={profile.name}
              ownerAvatar={profile.avatar_url}
              onLikeToggled={(photoId, likedNow, likesCount) => setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, liked_by_me: likedNow, likes_count: likesCount } : p))}
              onLocationUpdated={() => {}}
              onViewed={photoId => setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, views_count: (p.views_count || 0) + 1 } : p))}
              onDeleted={() => {}}
            />
          )}
        </div>
      </div>

      {/* Sidebar droite */}
      <div style={{ display: isMobile ? "none" : "block", width: 280, flexShrink: 0, overflowY: "auto", padding: "20px 20px 20px 0", borderLeft: "1px solid #F0F0F0" }}>
        <div style={{ ...card, marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Score Social</div>
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <div style={{ width: 80, height: 80, borderRadius: "50%", backgroundColor: "#F5F5F5", border: "4px solid #0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 8px", fontSize: 24, fontWeight: 800, color: "#0a0a0a" }}>{formatScore(profile.score)}</div>
            <div style={{ fontSize: 12, color: "#999" }}>sur 10</div>
          </div>
          <button onClick={() => navigate("/rating")} style={{ width: "100%", backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 12, padding: "10px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Évaluer {profile.name}
          </button>
        </div>

        {common.length > 0 && (
          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>En commun</div>
            {common.map((p, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid #F5F5F5" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#0a0a0a", flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: "#555" }}>{tTax(lang, p.label)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {showDatePrompt && (
        <DatePromptSheet
          lang={lang}
          onInvite={() => { setShowDatePrompt(false); setShowDateOrganizer(true); }}
          onClose={() => setShowDatePrompt(false)}
        />
      )}
      {showDateOrganizer && id && (
        <DateOrganizerSheet
          lang={lang}
          myLat={user?.latitude ? Number(user.latitude) : null}
          myLng={user?.longitude ? Number(user.longitude) : null}
          onSend={dateState.sendInvitation}
          onClose={() => setShowDateOrganizer(false)}
        />
      )}
      {showDateResponse && dateState.invitation && (
        <DateResponseSheet
          lang={lang}
          invitation={dateState.invitation}
          onAccept={() => dateState.accept(dateState.invitation!.id)}
          onDecline={() => dateState.decline(dateState.invitation!.id)}
          onClose={() => setShowDateResponse(false)}
        />
      )}
      {coupleNotice && (
        <div style={{ position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: isMobile ? 78 : 30, zIndex: 3200, backgroundColor: "#1c1c1e", color: "white", padding: "9px 16px", borderRadius: 20, fontSize: 12.5, fontWeight: 600, boxShadow: "0 4px 16px rgba(0,0,0,0.25)", maxWidth: "88%", textAlign: "center" }}>
          {coupleNotice}
        </div>
      )}
    </div>
  );
}
